import React, { useState, useEffect, useContext } from "react";
import { Link, withRouter } from "react-router-dom";
import qs from "qs";
import PropTypes from "prop-types";
import ReactRouterPropTypes from "react-router-prop-types";
import {
  Button,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import { HealthStreamLogo } from "components/Images";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faCaretDown,
  faSearch,
  faShoppingCart,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "components/Auth";
import { CartContext } from "components/CartContext";
import { StoreContext } from "components/StoreContext";
import Catalog from "components/Catalog";
import Error500 from "components/Error500";
import Loading from "components/Loading";
import Banner from "components/Site/Banner";
import UserAvatar from "./UserAvatar";
import Category from "./Category";
import Footer from "./Footer";

const Site = ({ history, location, children }) => {
  const authContext = useContext(AuthContext);
  const {
    authorizing: authInProgress,
    user,
    signIn,
    signOut,
    authorized: loggedIn
  } = authContext;
  const name = user ? user.given_name : "";
  const { config, store, categories } = useContext(StoreContext);
  const { cart, clearCart, clearCustomer } = useContext(CartContext);
  const [catalogVisible, setCatalogVisible] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [mobileNavVisible, setMobileNavVisible] = useState(false);
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hideBanner, setHideBanner] = useState(true);

  const handleBannerClick = () => {
    setHideBanner(true);
    if (localStorage) {
      localStorage.setItem("hideUpdatedStoreBanner", true);
    }
  };

  const handleChange = e => {
    setSearchTerm(e.target.value);
  };

  const handleEnter = e => {
    if (e.key === "Enter") {
      setSearchTerm(setSearchTerm);
      setMobileSearchVisible(false);
      history.push(`/search?keyword=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleSignOut = () => {
    signOut();
    clearCart();
    clearCustomer();
    history.push("/");
  };

  useEffect(() => {
    const body = document.getElementsByTagName("body")[0];
    if (catalogVisible || mobileNavVisible || mobileSearchVisible) {
      body.classList.add("is-fixed");
    } else {
      body.classList.remove("is-fixed");
    }
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    setSearchTerm(params.keyword || "");
  }, [
    location,
    catalogVisible,
    mobileNavVisible,
    mobileSearchVisible,
    hideBanner
  ]);

  // if route changes, close open navs
  history.listen(() => {
    setCatalogVisible(false);
    setMobileNavVisible(false);
  });

  window.addEventListener("resize", () => {
    setMobileNavVisible(false);
    setCatalogVisible(false);
  });

  if (config.loading || store.loading) {
    return (
      <div className="site">
        <Loading bg="transparent" />
      </div>
    );
  }

  if (config.error || store.error) {
    return <Error500 />;
  }

  return (
    <div className={catalogVisible ? "site has-catalog" : "site"}>
      {authInProgress && <div id="authOverlay" />}
      <header className="site__header">
        <div className="site__header-container">
          <Link
            to="/"
            className="site__header-home"
            title="Homepage"
            aria-label="HealthStream Store Home"
          >
            <HealthStreamLogo
              className="site__header-logo"
              alt="Logo: HealthStream"
            />
          </Link>

          <nav className="site__nav site__nav--lg" role="navigation">
            <Button
              color="link"
              aria-label="Catalog"
              onClick={() => setCatalogVisible(!catalogVisible)}
            >
              <span>Explore Catalog</span>
              <FontAwesomeIcon icon={faCaretDown} />
            </Button>
            <div className="site__search">
              <label htmlFor="search" className="site-search-label">
                Search
                <input
                  id="search"
                  name="search"
                  type="text"
                  className="form-control"
                  placeholder="Search for courses by subject, audience, or setting"
                  value={searchTerm}
                  onKeyDown={handleEnter}
                  onChange={handleChange}
                />
              </label>
              <a
                href={`/search?keyword=${encodeURIComponent(searchTerm)}`}
                id="searchIcon"
                className="btn btn-link"
                aria-label="Search"
              >
                <FontAwesomeIcon icon={faSearch} />
              </a>
            </div>

            <Link className="btn btn-link" to="/contact-us">
              For Organizations
            </Link>

            <div className="site__nav-divider" />

            <Link
              to="/cart"
              className="btn btn-link site__cart"
              aria-label="Cart"
            >
              {cart.count > 0 && (
                <span className="site__cart-count">
                  <span>{cart.count}</span>
                </span>
              )}
              <FontAwesomeIcon icon={faShoppingCart} />
            </Link>

            {loggedIn ? (
              <ButtonDropdown
                isOpen={userDropdown}
                toggle={() => setUserDropdown(!userDropdown)}
              >
                <DropdownToggle className="btn-auth">
                  <UserAvatar name={name} />
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem
                    onClick={() => {
                      history.push("/orders");
                    }}
                    data-test="button__purchase-history"
                  >
                    Purchase History
                  </DropdownItem>
                  <DropdownItem
                    onClick={handleSignOut}
                    data-test="button__sign-out"
                  >
                    Sign Out
                  </DropdownItem>
                </DropdownMenu>
              </ButtonDropdown>
            ) : (
              <Button
                onClick={() => {
                  signIn();
                }}
                aria-label="Sign In"
                className="btn-auth"
                data-test="button__sign-in"
              >
                <span>Sign In</span>
              </Button>
            )}
          </nav>

          <nav className="site__nav site__nav--md" role="navigation">
            <Button
              color="link"
              aria-label="Catalog"
              onClick={() => setCatalogVisible(!catalogVisible)}
            >
              <span>Explore Catalog</span>
              <FontAwesomeIcon icon={faCaretDown} />
            </Button>
            <Link className="btn btn-link" to="/contact-us">
              For Organizations
            </Link>
            <div className="site__nav-divider" />
            <Button
              color="link"
              aria-label="Toggle Search Bar"
              onClick={() => setMobileSearchVisible(!mobileSearchVisible)}
            >
              <FontAwesomeIcon icon={faSearch} size="lg" />
            </Button>
            <Link
              to="/cart"
              className="btn btn-link site__cart"
              aria-label="Cart"
            >
              {cart.count > 0 && (
                <span className="site__cart-count">
                  <span>{cart.count}</span>
                </span>
              )}
              <FontAwesomeIcon icon={faShoppingCart} size="lg" />
            </Link>
            <Button
              color="link"
              aria-label="Menu"
              onClick={() => setMobileNavVisible(!mobileNavVisible)}
            >
              <FontAwesomeIcon
                icon={mobileNavVisible ? faTimes : faBars}
                size="lg"
                fixedWidth
              />
            </Button>
          </nav>

          <nav className="site__nav site__nav--sm" role="navigation">
            <Button
              color="link"
              aria-label="Toggle Search Bar"
              onClick={() => setMobileSearchVisible(!mobileSearchVisible)}
            >
              <FontAwesomeIcon icon={faSearch} size="lg" />
            </Button>
            <Link
              to="/cart"
              className="btn btn-link site__cart"
              aria-label="Cart"
            >
              {cart.count > 0 && (
                <span className="site__cart-count">
                  <span>{cart.count}</span>
                </span>
              )}
              <FontAwesomeIcon icon={faShoppingCart} />
            </Link>
            <Button
              color="link"
              aria-label="Menu"
              onClick={() => setMobileNavVisible(!mobileNavVisible)}
            >
              <FontAwesomeIcon
                icon={mobileNavVisible ? faTimes : faBars}
                size="lg"
                fixedWidth
              />
            </Button>
          </nav>
          <nav
            className={`site__nav site__nav--overlay${
              mobileNavVisible ? " is-active" : ""
            }`}
            role="navigation"
          >
            <ul>
              <li>
                <Link to="/contact-us" className="site__nav-overlay-link">
                  For Organizations
                </Link>
              </li>
              <li>
                <Link to="/cart" className="site__nav-overlay-link">
                  <span>View Cart</span>
                  {cart.count > 0 && (
                    <span className="ms-2">
                      {cart.count === 1
                        ? `(${cart.count} item)`
                        : `(${cart.count} items)`}
                    </span>
                  )}
                </Link>
              </li>
              {!loggedIn && (
                <li>
                  <button
                    type="button"
                    className="site__nav-overlay-link"
                    data-test="button__sign-in"
                    onClick={() => {
                      signIn();
                    }}
                  >
                    Sign In
                  </button>
                </li>
              )}
              {loggedIn && (
                <li>
                  <Link
                    className="site__nav-overlay-link"
                    to="/orders"
                    data-test="link__purchase-history"
                  >
                    Purchase History
                  </Link>
                </li>
              )}
              {categories.tree.map(category => (
                <Category
                  key={category.id}
                  text={category.text}
                  path={category.path}
                  children={category.children}
                />
              ))}
              <li>
                <Link to="/catalog" className="site__nav-overlay-link">
                  Browse Entire Catalog
                </Link>
              </li>
              {loggedIn && (
                <li>
                  <Button
                    className="site__nav-overlay-link"
                    onClick={handleSignOut}
                    data-test="button__sign-out"
                  >
                    Sign Out
                  </Button>
                </li>
              )}
            </ul>
          </nav>
          <nav
            className={`site__nav site__nav--search${
              mobileSearchVisible ? " is-active" : ""
            }`}
            role="navigation"
          >
            <div className="site__searchbar">
              <FontAwesomeIcon
                icon={faSearch}
                className="site__searchbar-icon"
              />
              <label htmlFor="searchMobile" className="site-search-label">
                Search
                <input
                  id="searchMobile"
                  name="searchMobile"
                  type="text"
                  className="form-control site__searchbar-input"
                  placeholder="Search for courses"
                  value={searchTerm}
                  onKeyDown={handleEnter}
                  onChange={handleChange}
                />
              </label>
              <Button
                color="link"
                size="lg"
                aria-label="Close Search"
                onClick={() => setMobileSearchVisible(false)}
                className="site__searchbar-close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </nav>
          <nav
            className={`site__nav site__nav--catalog${
              catalogVisible ? " is-active" : ""
            }`}
            role="presentation"
            onClick={() => setCatalogVisible(false)}
          >
            <Catalog />
          </nav>
        </div>
      </header>
      {hideBanner === false && (
        <Banner hideBanner={hideBanner} handleBannerClick={handleBannerClick} />
      )}
      <div className="site__body">{children}</div>

      <Footer />

      <input
        type="hidden"
        id="app-version-field-id"
        value={config.appVersion}
      />
    </div>
  );
};

Site.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  children: PropTypes.node.isRequired
};

export default withRouter(Site);
